import React, { useCallback, useContext, useState } from 'react';
import { Download, Info } from 'lucide-react';

import { fbmSubstitutionRulesV1Api } from 'api/fbmSubstitutionRulesV1';
import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { cn } from 'shadcn/lib/utils';
import {
    BULK_UPLOAD_STATUS,
    BulkUploadDropzone,
    BulkUploadPendingChanges,
    BulkUploadRowFilterSwitch,
    BulkUploadStatus,
    BulkUploadTable,
    useBulkUpload,
} from 'shared/components/bulkUpload';

const SUBSTITUTION_RULE_COLUMN_DEFS = [
    { key: 'originalExternalSku', label: 'Original External SKU', required: true },
    { key: 'substituteExternalSku', label: 'Substitute External SKU', required: true },
    { key: 'substituteQuantity', label: 'Substitute Quantity', required: true },
];

function downloadCsv(filename, content) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function BulkSubstitutionRuleUpload() {
    const { fbmAccount } = useContext(UserContext);
    const fbmAccountName = fbmAccount?.fbmAccountName || '';

    const api = {
        edit: (key, params, rowsToUpdate, rowsToDelete) =>
            fbmSubstitutionRulesV1Api.bulkEdit(key, params.fbmAccountName, rowsToUpdate, rowsToDelete),
        fetch: (key, params, signal) => fbmSubstitutionRulesV1Api.bulkFetch(key, params.fbmAccountName, signal),
        persist: (key, params) => fbmSubstitutionRulesV1Api.bulkPersist(key, params.fbmAccountName),
        upload: (file, params) => fbmSubstitutionRulesV1Api.bulkUpload(file, params.fbmAccountName),
    };

    const bulkUpload = useBulkUpload({
        api,
        params: { fbmAccountName },
    });

    const [persistSuccess, setPersistSuccess] = useState(false);

    const handleDownloadTemplate = useCallback(() => {
        const headers = SUBSTITUTION_RULE_COLUMN_DEFS.map((col) => `${col.label}${col.required ? '*' : ''}`).join(',');
        const startDate = new Date();
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 1);
        const exampleRow = {
            originalExternalSku: 12345,
            substituteExternalSku: 67890,
            substituteQuantity: 2,
        };
        const exampleValues = SUBSTITUTION_RULE_COLUMN_DEFS.map((col) => exampleRow[col.key] ?? '').join(',');

        downloadCsv('substitution_rule_bulk_upload_template.csv', `${headers}\n${exampleValues}`);
    }, []);

    const handlePersist = useCallback(async () => {
        try {
            const result = await bulkUpload.persist();
            const hasErrors = result?.persistResults?.errors?.length > 0;
            if (!hasErrors) {
                setPersistSuccess(true);
                bulkUpload.reset();
            }
        } catch {
            // Error is already captured in bulkUpload.persistError.
        }
    }, [bulkUpload]);

    const handleStartNew = useCallback(() => {
        setPersistSuccess(false);
    }, []);

    const handleFileSelect = useCallback(
        (file) => {
            if (file && fbmAccountName) {
                bulkUpload.uploadFile(file);
            }
        },
        [bulkUpload, fbmAccountName],
    );

    const hasData = bulkUpload.rows.length > 0;
    const errorCount = bulkUpload.validationErrors.length;

    return (
        <div className={cn('mt-4 flex flex-col space-y-6', hasData && 'min-h-[calc(100dvh-10rem)]')}>
            <Card className={cn(hasData && 'flex min-h-0 min-w-0 flex-1 flex-col')}>
                <CardHeader>
                    <CardTitle>Bulk Substitution Rule Upload</CardTitle>
                    <CardDescription>
                        Upload a CSV file to create multiple substitution rules at once. The file will be validated
                        before you can save.
                        <br />
                        There is currently a limit of 1000 rows per bulk upload.
                    </CardDescription>
                </CardHeader>
                <CardContent className={cn('space-y-4', hasData && 'flex min-h-0 min-w-0 flex-1 flex-col')}>
                    {!hasData && !persistSuccess && (
                        <>
                            <div className="flex justify-end">
                                <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                                    <Download className="mr-2 h-4 w-4" />
                                    Download Template
                                </Button>
                            </div>
                            <BulkUploadDropzone
                                onFileSelect={handleFileSelect}
                                disabled={
                                    !fbmAccountName ||
                                    [BULK_UPLOAD_STATUS.LOADING, BULK_UPLOAD_STATUS.UPLOADING].includes(
                                        bulkUpload.status,
                                    )
                                }
                                isUploading={bulkUpload.status === BULK_UPLOAD_STATUS.UPLOADING}
                            />
                        </>
                    )}

                    <BulkUploadStatus
                        entityName="Substitution Rule"
                        fetchError={bulkUpload.fetchError}
                        handleStartNew={handleStartNew}
                        hasData={hasData}
                        persistError={bulkUpload.persistError}
                        persistErrors={bulkUpload.persistErrors}
                        persistSuccess={persistSuccess}
                        status={bulkUpload.status}
                        uploadError={bulkUpload.uploadError}
                    />

                    {hasData &&
                        !persistSuccess &&
                        !bulkUpload.hasPersistErrors &&
                        ![BULK_UPLOAD_STATUS.SUBMITTING, BULK_UPLOAD_STATUS.PERSISTING].includes(bulkUpload.status) && (
                            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        {errorCount > 0 ? (
                                            <Alert variant="destructive" className="inline-flex">
                                                {errorCount} validation error{errorCount !== 1 ? 's' : ''} found. Please
                                                fix the errors before saving. You can hover over the highlighted cells
                                                for more information.
                                            </Alert>
                                        ) : (
                                            <Alert className="inline-flex border-green-500 text-green-700">
                                                No validation errors. Ready to save.
                                            </Alert>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                            {bulkUpload.showIssuesFilterHint && (
                                                <Alert
                                                    className="w-full max-w-md border-sky-200 bg-sky-50 py-2 pr-3 text-sm text-sky-950 sm:w-auto dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-50"
                                                    data-testid="bulk-substitution-rule-filtered-view-hint"
                                                    role="status"
                                                >
                                                    <Info aria-hidden className="text-sky-600 dark:text-sky-400" />
                                                    <AlertDescription className="text-sky-900 dark:text-sky-100">
                                                        Showing only rows with validation issues, warnings, pending
                                                        edits, or deletes. Turn off the switch to see all rows.
                                                    </AlertDescription>
                                                </Alert>
                                            )}
                                            {bulkUpload.showIssuesRowFilter && (
                                                <BulkUploadRowFilterSwitch
                                                    checked={bulkUpload.showIssuesOnly}
                                                    disabled={bulkUpload.rows.length === 0}
                                                    onCheckedChange={bulkUpload.setShowIssuesOnly}
                                                />
                                            )}
                                        </div>
                                        {bulkUpload.hasUnsavedChanges && (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={bulkUpload.clearPendingChanges}
                                                >
                                                    Discard Changes
                                                </Button>
                                                <Button variant="default" size="sm" onClick={bulkUpload.submitChanges}>
                                                    Submit Changes
                                                </Button>
                                            </>
                                        )}
                                        <Button variant="outline" size="sm" onClick={bulkUpload.reset}>
                                            Start Over
                                        </Button>
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={handlePersist}
                                            disabled={!bulkUpload.canPersist}
                                        >
                                            Save Substitution Rules
                                        </Button>
                                    </div>
                                </div>
                                <BulkUploadTable
                                    columnDefs={SUBSTITUTION_RULE_COLUMN_DEFS}
                                    errorsByCell={bulkUpload.errorsByCell}
                                    fillAvailableHeight
                                    pendingDeletes={bulkUpload.pendingDeletes}
                                    pendingEdits={bulkUpload.pendingEdits}
                                    rows={bulkUpload.tableRows}
                                    toggleRowDelete={bulkUpload.toggleRowDelete}
                                    updateCell={bulkUpload.updateCell}
                                />
                                <BulkUploadPendingChanges
                                    pendingDeletes={bulkUpload.pendingDeletes}
                                    pendingEdits={bulkUpload.pendingEdits}
                                    rowsLength={bulkUpload.rows.length}
                                />
                            </div>
                        )}

                    {!fbmAccountName && (
                        <p className="text-sm text-muted-foreground">
                            Please select an FBM account above to upload files.
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export { BulkSubstitutionRuleUpload, SUBSTITUTION_RULE_COLUMN_DEFS };
